/**
 * Implements software requirements: SRS-215
 *
 * Serialises the guarded mutations of one case: two concurrent mutations would name the same
 * version, and the server would refuse the second as stale.
 */

import { freePromise } from '@/util/freePromise';

export type ReleaseSlot = () => void;

/** Per case: settles when the last queued mutation releases its slot. */
const _tails = new Map<number, Promise<void>>();

/** Call the returned release when the request settles, whatever the outcome. It is idempotent. */
export async function acquireCaseMutationSlot(caseId: number): Promise<ReleaseSlot> {
    const previous = _tails.get(caseId) ?? Promise.resolve();

    const { promise: settled, resolve: releaseSettled } = freePromise<void>();
    const tail = previous.then(() => settled);
    _tails.set(caseId, tail);

    await previous;

    let released = false;
    return () => {
        if (released) return;
        released = true;
        releaseSettled();
        if (_tails.get(caseId) === tail) _tails.delete(caseId);
    };
}

/** For tests only. */
export function resetCaseMutationQueue(): void {
    _tails.clear();
}
